import React from 'react';
import classes from "./MatchWinningsInfoPage2.module.css";
import { FaArrowLeft, FaCheck, FaClock } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

/* ─────────────────────────────────────────────────────────
   PRIZE CONFIG  —  update values to match your Vodacom tiers
───────────────────────────────────────────────────────── */
const PRIZE_TIERS = [
    {
        rank: 1,
        medal: "🥇",
        label: "1st Place",
        prizes: [
            { icon: "📱", type: "Smartphone", value: "Smartphone", desc: "Win a Smartphone" },
        ],
    },
    {
        rank: 2,
        medal: "🥈",
        label: "2nd Place",
        prizes: [
            { icon: "👕", type: "Jersey", value: "Jersey", desc: "Win a Jersey" },
        ],
    },
    {
        rank: 3,
        medal: "🥉",
        label: "3rd Place",
        prizes: [
            { icon: "💰", type: "Airtime", value: "Airtime - 2000 TSH", desc: "Added directly to your number" },
        ],
    },
];

const PRIZE_TIERS_SW = [
    {
        rank: 1,
        medal: "🥇",
        label: "Nafasi ya 1",
        prizes: [
            { icon: "📱", type: "Smartphone", value: "Simu Janja", desc: "Shinda Simu Janja (Smartphone)" },
        ],
    },
    {
        rank: 2,
        medal: "🥈",
        label: "Nafasi ya 2",
        prizes: [
            { icon: "👕", type: "Jersey", value: "Jezi", desc: "Shinda Jezi" },
        ],
    },
    {
        rank: 3,
        medal: "🥉",
        label: "Nafasi ya 3",
        prizes: [
            { icon: "💰", type: "Airtime", value: "Muda wa Maongezi - 2000 TSH", desc: "Itaongezwa moja kwa moja kwenye namba yako" },
        ],
    },
];

/* colour per rank tier */
const tierAccent = (rank) => {
    if (rank === 1) return { border: "rgba(240,180,41,0.4)", bg: "rgba(240,180,41,0.07)", text: "#f0b429" };
    if (rank === 2) return { border: "rgba(180,180,200,0.35)", bg: "rgba(180,180,200,0.05)", text: "#c0c0d0" };
    if (rank === 3) return { border: "rgba(180,100,40,0.35)", bg: "rgba(180,100,40,0.06)", text: "#cd7f32" };
    return { border: "rgba(230,0,0,0.18)", bg: "rgba(230,0,0,0.04)", text: "rgba(255,255,255,0.5)" };
};

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const MatchWinningsInfoPage2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location?.state ?? {};

    const { t } = useTranslation();

    const { selectedLanguage } = useSelector((state) => state.languageSlice);

    const {
        user_rank, total_points, users_joined,
        rewarded, message,
        home_team_name, home_team_image,
        away_team_name, away_team_image,
        league_name, match_date_time,
    } = state;

    /* find this user's tier */
    const myTier = PRIZE_TIERS.find(t =>
        user_rank >= t.rank && user_rank <= (t.rangeEnd ?? t.rank)
    );

    const isRewarded = !!rewarded;
    const hasRank = !!user_rank;

    return (
        <div className={classes.page}>
            <div className={classes.bgGlow} />

            {/* ══ HEADER ══ */}
            <div className={classes.header}>
                <button className={classes.backBtn} onClick={() => navigate(-1)}>
                    <FaArrowLeft />
                </button>
                <span className={classes.headerTitle}>{t("231")}</span>
                <div style={{ width: 38 }} />
            </div>

            <div className={classes.content}>

                {/* ── MATCH CONTEXT PILL ── */}
                {(home_team_name || league_name) && (
                    <div className={classes.matchPill}>
                        {home_team_image && (
                            <img src={home_team_image} alt={home_team_name} className={classes.pillLogo} />
                        )}
                        <div className={classes.pillMid}>
                            {league_name && <span className={classes.pillLeague}>{league_name}</span>}
                            {home_team_name && away_team_name && (
                                <span className={classes.pillTeams}>{home_team_name} {t("232")} {away_team_name}</span>
                            )}
                            {match_date_time && (
                                <span className={classes.pillDate}>
                                    {moment.utc(match_date_time).local().format("DD MMM YYYY")}
                                </span>
                            )}
                        </div>
                        {away_team_image && (
                            <img src={away_team_image} alt={away_team_name} className={classes.pillLogo} />
                        )}
                    </div>
                )}

                {/* ── YOUR PRIZE CARD  (only if user has a rank) ── */}
                {hasRank && myTier && (
                    <div className={classes.myPrizeCard} style={{ borderColor: tierAccent(user_rank).border }}>

                        {/* glowing top bar */}
                        <div className={classes.myPrizeBar} style={{ background: tierAccent(user_rank).border }} />

                        {/* rank + status row */}
                        <div className={classes.myPrizeTop}>
                            <div className={classes.myPrizeMedal}>{myTier.medal}</div>
                            <div className={classes.myPrizeInfo}>
                                <span className={classes.myPrizeLabel}>{myTier.label}</span>
                                <span className={classes.myPrizeSub}>
                                    {total_points?.toLocaleString()} {t("233")} · {users_joined?.toLocaleString()} {t("234")}
                                </span>
                            </div>
                            <div className={`${classes.statusBadge} ${isRewarded ? classes.statusDone : classes.statusPending}`}>
                                {isRewarded
                                    ? <><FaCheck style={{ fontSize: 10 }} /> {t("235")}</>
                                    : <><FaClock style={{ fontSize: 10 }} /> {t("236")}</>
                                }
                            </div>
                        </div>

                        {/* prizes list */}
                        <div className={classes.myPrizeList}>
                            {myTier.prizes.map((p, i) => (
                                <div key={i} className={classes.myPrizeRow}>
                                    <div className={classes.myPrizeIconWrap}
                                        style={{ background: tierAccent(user_rank).bg, borderColor: tierAccent(user_rank).border }}>
                                        <span className={classes.myPrizeIcon}>{p.icon}</span>
                                    </div>
                                    <div className={classes.myPrizeText}>
                                        <span className={classes.myPrizeType}>{p.type}</span>
                                        <span className={classes.myPrizeDesc}>{p.desc}</span>
                                    </div>
                                    <span className={classes.myPrizeValue}
                                        style={{ color: tierAccent(user_rank).text }}>
                                        {p.value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* admin message */}
                        {message && (
                            <div className={classes.adminMsg}>
                                <span>📣</span>
                                <span>{message}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── ALL TIERS TABLE ── */}
                <div className={classes.tiersCard}>
                    <div className={classes.tiersHead}>
                        <span className={classes.tiersTitle}>{t("237")}</span>
                        <span className={classes.tiersSub}>{t("238")}</span>
                    </div>

                    {(selectedLanguage == 0 ? PRIZE_TIERS : PRIZE_TIERS_SW).map((tier) => {
                        const accent = tierAccent(tier.rank);
                        const isMine = hasRank &&
                            user_rank >= tier.rank &&
                            user_rank <= (tier.rangeEnd ?? tier.rank);

                        return (
                            <div
                                key={tier.rank}
                                className={`${classes.tierRow} ${isMine ? classes.tierRowActive : ""}`}
                                style={isMine ? { borderLeftColor: accent.border } : {}}
                            >
                                {/* rank medal */}
                                <div className={classes.tierMedalWrap}>
                                    <span className={classes.tierMedal}
                                        style={{ color: isMine ? accent.text : undefined }}>
                                        {tier.medal}
                                    </span>
                                </div>

                                {/* label + prizes */}
                                <div className={classes.tierBody}>
                                    <span className={classes.tierLabel}
                                        style={{ color: isMine ? accent.text : undefined }}>
                                        {tier.label}
                                    </span>
                                    <div className={classes.tierChips}>
                                        {tier.prizes.map((p, i) => (
                                            <span key={i} className={classes.tierChip}
                                                style={isMine ? { borderColor: accent.border, color: accent.text, background: accent.bg } : {}}>
                                                {p.icon} {p.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* YOU badge */}
                                {isMine && (
                                    <span className={classes.youBadge}
                                        style={{ background: accent.text, color: "#090000" }}>
                                        {t("239")}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── HOW IT WORKS ── */}
                <div className={classes.howCard}>
                    <p className={classes.howTitle}>{t("240")}</p>
                    <div className={classes.howList}>
                        {[
                            { icon: "🎯", text: t("241") },
                            { icon: "🏁", text: t("242") },
                            { icon: "🏆", text: t("243") },
                            { icon: "📲", text: t("244") },
                            { icon: "⏱", text: t("245") },
                        ].map((item, i) => (
                            <div key={i} className={classes.howRow}>
                                <span className={classes.howIcon}>{item.icon}</span>
                                <span className={classes.howText}>{item.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MatchWinningsInfoPage2;